import { useCallback, useEffect, useState } from "react";
import { getToken, getUserId } from "../utils/auth";
import LeetCodeLanguageStats from "../components/LeetCodeLanguageStats";
import "./LeetCodeProfilePage.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

// Mock maximums for standard progress bar calculations if backend doesn't provide them.
const ESTIMATED_TOTAL_EASY = 800;
const ESTIMATED_TOTAL_MEDIUM = 1600;
const ESTIMATED_TOTAL_HARD = 700;

export default function LeetCodeProfilePage() {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [missingUrl, setMissingUrl] = useState(false);
  const [leetcodeUrl, setLeetcodeUrl] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  
  // State to manage editing existing URL
  const [isEditing, setIsEditing] = useState(false);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError("");
    setMissingUrl(false);
    setMessage("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/student/get-leetcode-profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ studentId: getUserId() }),
      });

      const data = await response.json();

      if (response.status === 400 && data?.message?.includes("Leetcode URL")) {
        setMissingUrl(true);
        return;
      }

      if (!response.ok) {
        throw new Error(data?.message || "Unable to load LeetCode profile.");
      }

      setProfile(data);
    } catch (err) {
      console.error("LeetCode profile fetch error", err);
      setError(err.message || "Could not fetch LeetCode profile.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSaveUrl = async (e) => {
    e.preventDefault();
    if (!leetcodeUrl.trim()) {
      setError("Please enter a valid LeetCode profile URL.");
      return;
    }

    setSubmitLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/student/update-leetcode-url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ userId: getUserId(), leetcodeUrl: leetcodeUrl.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Unable to save LeetCode URL.");
      }

      setMessage("Account linked successfully! Synchronizing data...");
      setLeetcodeUrl("");
      setIsEditing(false);
      
      // Delay briefly for server sync
      setTimeout(async () => {
        await fetchProfile();
      }, 1200);
    } catch (err) {
      console.error("LeetCode URL save error", err);
      setError(err.message || "Could not save LeetCode URL.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleStartEdit = () => {
    const existingUrl = profile?.url || profile?.leetcodeUrl || profile?.profileUrl || profile?.leetcodeURL || "";
    setLeetcodeUrl(existingUrl);
    setIsEditing(true);
    setError("");
    setMessage("");
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setLeetcodeUrl("");
    setError("");
  };

  const getDifficultyCount = (list, diff) => {
    return list?.find((q) => q.difficulty.toUpperCase() === diff.toUpperCase())?.count ?? 0;
  };

  const getDifficultyPercent = (list, diff) => {
    return list?.find((q) => q.difficulty.toUpperCase() === diff.toUpperCase())?.percentage ?? 0;
  };

  const renderDashboard = () => {
    if (!profile) return null;

    const accepted = profile.profile?.numAcceptedQuestions || [];
    const beats = profile.profile?.userSessionBeatsPercentage || [];

    const easySolved = getDifficultyCount(accepted, "EASY");
    const mediumSolved = getDifficultyCount(accepted, "MEDIUM");
    const hardSolved = getDifficultyCount(accepted, "HARD");
    const totalSolved = easySolved + mediumSolved + hardSolved;

    const globalBeats = profile.profile?.totalQuestionBeatsPercentage || 0;
    const profileUrl = profile.url || profile.leetcodeUrl || profile.profileUrl || profile.leetcodeURL;
    const userRating = profile.rating ?? profile.leetCodeRating;

    return (
      <div className="lc-dashboard-layout">
        {/* Metric Overview Row */}
        <div className="lc-metrics-grid">
          <div className="lc-stat-card total-card">
            <div className="lc-card-header">
              <span className="lc-card-icon">🎯</span>
              <span className="lc-card-label">Total Solved</span>
            </div>
            <h2 className="lc-card-value">{totalSolved}</h2>
            <p className="lc-card-subtext">Across all complexities</p>
          </div>

          <div className="lc-stat-card ranking-card">
            <div className="lc-card-header">
              <span className="lc-card-icon">🏆</span>
              <span className="lc-card-label">Contest Rating</span>
            </div>
            <h2 className="lc-card-value">{userRating && userRating !== "N/A" ? Math.round(userRating) : "—"}</h2>
            <p className="lc-card-subtext">{userRating && userRating !== "N/A" ? "Active Competitor" : "No active rating"}</p>
          </div>

          <div className="lc-stat-card beats-card">
            <div className="lc-card-header">
              <span className="lc-card-icon">⚡</span>
              <span className="lc-card-label">Global Performance</span>
            </div>
            <h2 className="lc-card-value">{Number(globalBeats).toFixed(1)}%</h2>
            <p className="lc-card-subtext">Beats global community</p>
          </div>
        </div>

        {/* Detailed Metrics Split */}
        <div className="lc-details-split">
          {/* Progress Bars */}
          <div className="lc-panel-card">
            <h3 className="lc-panel-title">Difficulty Breakdown</h3>
            <div className="lc-progress-stack">
              <div className="lc-progress-group easy">
                <div className="lc-progress-meta">
                  <span className="difficulty-tag">Easy</span>
                  <span className="difficulty-count"><strong>{easySolved}</strong> / {ESTIMATED_TOTAL_EASY}</span>
                </div>
                <div className="lc-progress-track-bar">
                  <div className="lc-progress-fill" style={{ width: `${Math.min((easySolved / ESTIMATED_TOTAL_EASY) * 100, 100)}%` }}></div>
                </div>
              </div>

              <div className="lc-progress-group medium">
                <div className="lc-progress-meta">
                  <span className="difficulty-tag">Medium</span>
                  <span className="difficulty-count"><strong>{mediumSolved}</strong> / {ESTIMATED_TOTAL_MEDIUM}</span>
                </div>
                <div className="lc-progress-track-bar">
                  <div className="lc-progress-fill" style={{ width: `${Math.min((mediumSolved / ESTIMATED_TOTAL_MEDIUM) * 100, 100)}%` }}></div>
                </div>
              </div>

              <div className="lc-progress-group hard">
                <div className="lc-progress-meta">
                  <span className="difficulty-tag">Hard</span>
                  <span className="difficulty-count"><strong>{hardSolved}</strong> / {ESTIMATED_TOTAL_HARD}</span>
                </div>
                <div className="lc-progress-track-bar">
                  <div className="lc-progress-fill" style={{ width: `${Math.min((hardSolved / ESTIMATED_TOTAL_HARD) * 100, 100)}%` }}></div>
                </div>
              </div>
            </div>
          </div>

          {/* Session Performance Card */}
          <div className="lc-panel-card">
            <h3 className="lc-panel-title">Accuracy Percentile Beats</h3>
            <div className="lc-badges-list">
              <div className="lc-badge-row easy">
                <div className="badge-identity">
                  <span className="badge-marker"></span>
                  <span>Easy Speed / Accuracy</span>
                </div>
                <strong className="badge-metric">{parseFloat(getDifficultyPercent(beats, "EASY")).toFixed(1)}%</strong>
              </div>
              <div className="lc-badge-row medium">
                <div className="badge-identity">
                  <span className="badge-marker"></span>
                  <span>Medium Speed / Accuracy</span>
                </div>
                <strong className="badge-metric">{parseFloat(getDifficultyPercent(beats, "MEDIUM")).toFixed(1)}%</strong>
              </div>
              <div className="lc-badge-row hard">
                <div className="badge-identity">
                  <span className="badge-marker"></span>
                  <span>Hard Speed / Accuracy</span>
                </div>
                <strong className="badge-metric">{parseFloat(getDifficultyPercent(beats, "HARD")).toFixed(1)}%</strong>
              </div>
            </div>
          </div>
        </div>

        {profileUrl && <LeetCodeLanguageStats studentId={getUserId()} />}

        {/* Action Tray */}
        {profileUrl && (
          <div className="lc-action-tray">
            <a className="lc-external-btn" href={profileUrl} target="_blank" rel="noreferrer">
              <span>View Live Profile on LeetCode</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"/></svg>
            </a>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="leetcode-profile-page">
      <div className="profile-page-container">
        {/* Dynamic Header */}
        <div className="profile-header">
          <div className="header-meta">
            <h1>LeetCode Performance</h1>
            <p>Sync engine & live coding profile parameters</p>
          </div>
          
          <div className="header-actions">
            {profile && (profile.username || profile.name) && (
              <div className="user-pill">
                <span className="pill-dot"></span>
                <span className="pill-text">@{profile.username || profile.name}</span>
              </div>
            )}
            
            {/* Edit Profile Button */}
            {!loading && profile && !isEditing && (
              <button className="lc-edit-btn" onClick={handleStartEdit}>
                ✏️ Edit Profile URL
              </button>
            )}
          </div>
        </div>

        {/* Status / Message Notifications */}
        {message && <div className="lc-banner status-msg">{message}</div>}
        {error && <div className="lc-banner error-msg">{error}</div>}

        {/* Loading State */}
        {loading && (
          <div className="profile-loading">
            <div className="loader" />
            <p>Compiling database parameters...</p>
          </div>
        )}

        {/* Edit URL Form Card (When Editing) */}
        {!loading && isEditing && (
          <div className="missing-url-card edit-mode-card">
            <div className="card-accent-bar"></div>
            <h3>Update LeetCode Identity</h3>
            <p>Provide a new LeetCode URL to re-sync your profile stats with this workspace.</p>
            <form onSubmit={handleSaveUrl} className="input-row">
              <input
                type="url"
                required
                value={leetcodeUrl}
                placeholder="https://leetcode.com/u/yourusername/"
                onChange={(e) => setLeetcodeUrl(e.target.value)}
              />
              <div className="btn-group">
                <button type="submit" disabled={submitLoading}>
                  {submitLoading ? "Updating..." : "Save Changes"}
                </button>
                <button type="button" className="cancel-btn" onClick={handleCancelEdit} disabled={submitLoading}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Missing URL Initial Card */}
        {!loading && missingUrl && !isEditing && (
          <div className="missing-url-card">
            <div className="card-accent-bar"></div>
            <h3>Connect LeetCode Identity</h3>
            <p>Your workspace metrics are currently isolated from the engineering network leaderboard.</p>
            <form onSubmit={handleSaveUrl} className="input-row">
              <input
                type="url"
                required
                value={leetcodeUrl}
                placeholder="https://leetcode.com/u/yourusername/"
                onChange={(e) => setLeetcodeUrl(e.target.value)}
              />
              <button type="submit" disabled={submitLoading}>
                {submitLoading ? "Linking Core..." : "Establish Connection"}
              </button>
            </form>
          </div>
        )}

        {/* Dashboard and Empty States */}
        {!loading && profile && !isEditing && renderDashboard()}

        {!loading && !profile && !missingUrl && !error && !isEditing && (
          <div className="profile-empty-state">
            <div className="empty-icon">📭</div>
            <p>No active synchronization sequence detected.</p>
            <button onClick={fetchProfile} className="retry-btn">Force Sync Retrying</button>
          </div>
        )}
      </div>
    </div>
  );
}